import { INSIGHT } from './insight-gate/insight-rules.config';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  concat,
  debounceTime,
  distinctUntilChanged,
  filter,
  from,
  groupBy,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  OperatorFunction,
  share,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';

import { Option } from '@sapphire/result';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { UserSettingsSelectorsFacade } from '@ps/user-settings-data-access';
import { Nullable } from '@ps/utils';
import {
  activeSupplierIds,
  defaultAllocationsOfLocale,
  mapSurveyLocalization,
  totalAllocatedQuantity,
  AdvancedFeasibilityFilters,
  CHILD_FEASIBILITY_UNAVAILABLE_MESSAGE,
  feasibilityErrorMessage,
  ComputeFeasibilityParams,
  ComputeFeasibilityParamsForPreDraft,
  DefaultSupplierAllocation,
  FeasibilityRequester,
  FeasibilityEstimate,
  FlexibilityPrices,
  Qualification,
  SupplierAllocation,
  SupplierBlend,
  SupplierManagementService,
  Survey,
  SurveyKind,
  SurveyMetadataForm,
} from '@ps/survey-utils';
import {
  SupplierActions,
  SupplierApiActions,
  SupplierSelectorsFacade,
  SupplierService,
} from '@ps/survey-data-access';

import { FeasibilityService } from '../../services/feasibility/feasibility.service';
import { FlexibilityPricesService } from '../../services/flexibility-prices/flexibility-prices.service';
import { PurePriceCpiService } from '../../services/pure-price-cpi/pure-price-cpi.service';
import { SurveyScoreService } from '../../services/survey-score/survey-score.service';
import { SurveyCreationSelectorsFacade } from '../survey-creation.selectors.facade';

import {
  SurveyCreationCoreApiActions,
  SurveyCreationCoreChildrenApiActions,
} from '../core/survey-creation-core.actions';
import {
  SurveyCreationAudienceActions,
  SurveyCreationChildrenActions,
  SurveyCreationChildrenApiActions,
} from '../audience/survey-creation-audience.actions';
import {
  selectAdvancedFeasibilityFilters,
  selectAudienceChildren,
  selectIsPreDraft,
  selectMarginForm,
  selectMetadataForm,
  selectSurveyFormState,
} from '../audience/survey-creation-audience.selectors';
import {
  selectActiveChildKey,
  selectCoreChildren,
  selectRootSurveyId,
  selectSurvey,
  selectSurveyId,
} from '../core/survey-creation-core.selectors';

import {
  SurveyCreationReferenceActions,
  SurveyCreationReferenceApiActions,
} from '../reference/survey-creation-reference.actions';
import {
  SurveyChildRegistryState,
  SurveyCreationChildAudienceState,
} from '../survey-creation-state.model';
import { SurveyChildKey } from '../survey-creation-shared-state.model';
import { SurveyAudienceForm } from '../../utils';
import {
  appliedOfferPrice,
  computeFeasibilityParams,
  computeFeasibilityParamsForPreDraft,
  debounceByActionType,
  qualifiesForRecalculation,
  resolveRecalculationKeys,
  shouldMarkAsPending,
  targetsActiveSurvey,
  toRecalculationChildTasks,
  toSupplierContext,
} from './utils/survey-creation-recalculation.helper';
import {
  PurePriceOutcome,
  RecalculationData,
  RecalculationTrigger,
} from './utils/survey-creation-recalculation.types';

import {
  SurveyCreationInsightsActions,
  SurveyCreationInsightsApiActions,
  SurveyCreationInsightsPreDraftActions,
  SurveyCreationInsightsPreDraftApiActions,
} from './survey-creation-insights.actions';
import {
  selectInsightsChildren,
  selectPricing,
} from './survey-creation-insights.selectors';
import { InsightGate } from './insight-gate/insight-gate.service';

@Injectable()
export class SurveyCreationInsightsEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);

  private readonly _store = inject(Store);
  private readonly _insightGate = inject(InsightGate);

  private readonly _surveyScoreService = inject(SurveyScoreService);

  private readonly _feasibilityService = inject(FeasibilityService);

  private readonly _supplierService = inject(SupplierService);

  private readonly _flexibilityPricesService = inject(FlexibilityPricesService);
  private readonly _toastService = inject(ToastService);

  private readonly _purePriceCpiService = inject(PurePriceCpiService);

  private readonly _authSelectors = inject(AuthSelectorsFacade);

  private readonly _userSettingsSelectors = inject(UserSettingsSelectorsFacade);

  private readonly _surveyCreationSelectors = inject(
    SurveyCreationSelectorsFacade,
  );

  private readonly _supplierSelectors = inject(SupplierSelectorsFacade);

  private readonly _supplierEngine = inject(SupplierManagementService);

  public readonly computeScore$ = createEffect(() => {
    const surveyChangeTrigger$ = this._actions$.pipe(
      ofType(
        SurveyCreationAudienceActions.updateMetadata,
        // Score is priced on buyer CPI, so a settle re-asks; not updateMargin,
        // which fires per keystroke and doesn't feed the score at all.
        SurveyCreationAudienceActions.settleBuyerCpi,
        SurveyCreationAudienceActions.toggleManualProcessing,
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        SurveyCreationInsightsActions.refreshAfterSave,
        // A drawer CPI edit on the active row re-prices the score too.
        SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess,
      ),
      this._insightGate.onlyIfAllowed(INSIGHT.SCORE),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(
        ([action, surveyId]) =>
          !!surveyId && targetsActiveSurvey(action, surveyId),
      ),
      // Two branches read this (pending, result); share so the gate and the
      // selects run once per action.
      share(),
    );

    return merge(
      surveyChangeTrigger$.pipe(
        filter(([action]) => shouldMarkAsPending(action)),
        map(() => SurveyCreationInsightsApiActions.computeScorePending()),
      ),
      surveyChangeTrigger$.pipe(
        filter(([action]) =>
          qualifiesForRecalculation(
            action,
            !!this._userSettingsSelectors.buyerSettings()
              .enableManualProcessing,
          ),
        ),
        debounceTime(500),
        map(([, surveyId]) => Option.from(surveyId).unwrap()),
        concatLatestFrom(() => [
          this._store.select(selectMetadataForm),
          this._store.select(selectMarginForm),
        ]),
        filter(([surveyId, metadata]) => !!surveyId && !!metadata?.valid),
        map(([surveyId, metadata, margin]) => ({
          surveyId,
          cpi: Option.from(margin.value).mapOr(0, ({ buyerCpi }) =>
            Option.from(buyerCpi).unwrapOr(0),
          ),
          value: Option.from(metadata.value).map((value) => ({
            loi: Option.from(value.loi).unwrapOr(0),
            incidence: Option.from(value.incidence).unwrapOr(0),
          })),
        })),
        filter(({ value }) => value.isSome()),
        map(({ surveyId, cpi, value }) => ({
          surveyId,
          cpi,
          value: value.unwrap(),
        })),
        switchMap(({ surveyId, cpi, value }) =>
          this._surveyScoreService
            .computeScore(surveyId, {
              cpi,
              ir: value.incidence,
              loi: value.loi,
            })
            .pipe(
              map(({ score, scorePercentage }) =>
                SurveyCreationInsightsApiActions.computeScoreSuccess({
                  score,
                  scorePercentage,
                }),
              ),
              this.psCatchError((error) =>
                of(
                  SurveyCreationInsightsApiActions.computeScoreFailure({
                    error: error.message,
                  }),
                ),
              ),
            ),
        ),
      ),
    );
  });

  // Every trigger runs one pipeline, fanned out to the children it targets.
  public readonly recalculateFeasibility$ = createEffect(() => {
    const trigger$ = this._recalculationTrigger$();
    return merge(
      this._recalculationPendingActions$(trigger$),
      this._recalculationResultActions$(trigger$),
    );
  });

  public readonly preDraftFeasibility$ = createEffect(() => {
    const trigger$ = this._actions$.pipe(
      ofType(
        SurveyCreationInsightsPreDraftActions.calculateFeasibilityForPreDraft,
        SurveyCreationAudienceActions.updateQualifications,
        SurveyCreationAudienceActions.updateQuotas,
      ),
      debounceTime(50),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectIsPreDraft),
        this._store.select(selectAudienceChildren),
      ]),
      filter(([, surveyId, isPreDraft]) => !surveyId && isPreDraft),
      map(([, , , children]) => Object.entries(children)),
      share(),
    );

    const validEntries$ = trigger$.pipe(
      map((entries) =>
        entries
          .map(([key, child]) => ({
            key,
            form: SurveyAudienceForm.from({
              metadata: child.metadata,
              qualifications: child.qualifications,
              quotas: child.quotas,
            }),
          }))
          .filter(({ form }) => form.valid),
      ),
      share(),
    );

    return merge(
      validEntries$.pipe(
        mergeMap((entries) =>
          entries.map(({ key }) =>
            SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftPending(
              { key },
            ),
          ),
        ),
      ),
      validEntries$.pipe(
        mergeMap((entries) => {
          // Tracker's wave clones share identical params — dedupe per-trigger
          // so they share one HTTP call instead of N identical ones.
          const sharedRequests = new Map<
            string,
            Observable<FeasibilityEstimate>
          >();

          return entries.map(({ key, form }) => {
            const params = computeFeasibilityParamsForPreDraft(
              this._currentUser(),
              Option.from(form.data).unwrap(),
            );

            return {
              key,
              request$: this._sharedFeasibilityRequest(sharedRequests, params),
            };
          });
        }),
        groupBy(({ key }) => key),
        mergeMap((group$) =>
          group$.pipe(
            switchMap(({ key, request$ }) =>
              request$.pipe(
                map((estimate) =>
                  SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftSuccess(
                    { key, ...estimate },
                  ),
                ),
                this.psCatchError((error) =>
                  of(
                    SurveyCreationInsightsPreDraftApiActions.calculateFeasibilityForPreDraftFailure(
                      { key, error: error.message },
                    ),
                  ),
                ),
              ),
            ),
          ),
        ),
      ),
    );
  });

  // Seeds the supplier allocations slice when an existing survey is opened for
  // edit. Fetches the editable-scope allocations once off `loadActiveSurveyForEditSuccess`
  // and stores them via `loadAllocationsSuccess`, so downstream effects
  // (`redistributeOnCompletesChange$`) and the drawer read a populated slice.
  public readonly seedSuppliersOnEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      this._insightGate.onlyIfAllowed(INSIGHT.SUPPLIERS),
      concatLatestFrom(() => [
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
        this._store.select(selectSurvey),
      ]),
      filter(([, surveyId]) => !!surveyId),
      map(([, surveyId, formState, survey]) => ({
        surveyId: Option.from(surveyId).unwrap(),
        survey: Option.from(survey),
        form: SurveyAudienceForm.from(formState),
      })),
      filter(({ form }) => form.valid),
      map(({ surveyId, survey, form }) => {
        const formData = Option.from(form.data).unwrap();
        return {
          user: this._withEmail(this._surveyUser(survey)),
          surveyId,
          survey: Option.from(survey),
          ...formData,
          catalogQualifications: this._catalogQualifications(formData.metadata),
        };
      }),
      switchMap((data) =>
        this._supplierService
          .allocations(data.surveyId, {
            scope: 'editable',
            context: toSupplierContext(data),
          })
          .pipe(
            map((allocations) =>
              SupplierApiActions.loadAllocationsSuccess({ allocations }),
            ),
            this.psCatchError(
              (error) =>
                of(
                  SupplierApiActions.loadAllocationsFailure({
                    error:
                      error.message ?? 'Failed to load supplier allocations',
                  }),
                ),
              { notify: true },
            ),
          ),
      ),
    ),
  );

  public readonly syncActiveChild$ = createEffect(() =>
    this._store.select(selectSurveyId).pipe(
      distinctUntilChanged(),
      map((activeChildId) =>
        SurveyCreationInsightsActions.syncActiveChild({ activeChildId }),
      ),
    ),
  );

  // Re-spreads the supplier allocation quantities on every metadata update in
  // edit mode, using the current completes (goal). Persists locally through
  // `loadAllocationsSuccess` — no PUT.
  public readonly redistributeOnCompletesChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationAudienceActions.updateMetadata),
      concatLatestFrom(() => this._store.select(selectSurveyId)),
      filter(([, surveyId]) => !!surveyId),
      debounceTime(500),
      map(([{ metadata }]) =>
        Option.from(metadata.value?.completes).unwrapOr(0),
      ),
      filter((goal) => goal > 0),
      map((goal) => ({
        goal,
        current: this._supplierSelectors.allocations(),
      })),
      // Any metadata edit lands here, not only a completes change. A mix that
      // already adds up to the goal has nothing to redistribute, and spreading
      // it again would overwrite the buyer's saved allocation.
      filter(({ goal, current }) => totalAllocatedQuantity(current) !== goal),
      mergeMap(({ goal, current }) =>
        this._redistributedAllocations(current, goal).match({
          some: (allocations) => [
            SupplierApiActions.loadAllocationsSuccess({ allocations }),
          ],
          none: () => [],
        }),
      ),
    ),
  );

  // Loads the saved supplier blends for the survey's locale when an existing
  // survey opens for edit, so `redistributeOnCompletesChange$` can resolve the
  // applied blend even before the CPI / supplier drawer is opened.
  public readonly loadBlendsOnEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      this._insightGate.onlyIfAllowed(INSIGHT.SUPPLIERS),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      map(([, survey]) => Option.from(survey)),
      filter((survey) => survey.isSome()),
      map((survey) => survey.unwrap()),
      map((survey) =>
        SupplierActions.loadBlends({
          locale: mapSurveyLocalization(
            survey.countryCode,
            survey.languageShortCode,
          ),
        }),
      ),
    ),
  );

  // Default Allocation redistributes by the active locale's defaults, but the
  // supplier state only gets them when the drawer opens. Load them with the
  // survey, so a child switch never leaves the previous locale's list behind.
  public readonly loadDefaultAllocationsOnEdit$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess),
      this._insightGate.onlyIfAllowed(INSIGHT.SUPPLIERS),
      filter(
        () =>
          this._surveyCreationSelectors.suppliersMetaData()
            .supplierDefaultAllocation,
      ),
      concatLatestFrom(() => this._store.select(selectSurvey)),
      mergeMap(([, survey]) =>
        Option.from(survey).match({
          some: ({ countryCode, languageShortCode }) => [
            SupplierActions.loadDefaultAllocations({
              locale: mapSurveyLocalization(countryCode, languageShortCode),
            }),
          ],
          none: () => [],
        }),
      ),
    ),
  );

  // The update PUT's response already carries the new allocations, so this
  // re-runs only flexibility → feasibility, off the action, not a refetch.
  public readonly refreshPricingAfterSupplierUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SupplierApiActions.updateAllocationsSuccess),
      filter(({ skipFeasibilityRefresh }) => !skipFeasibilityRefresh),
      this._insightGate.onlyIfAllowed(INSIGHT.FEASIBILITY),
      concatLatestFrom(() => [
        this._store.select(selectActiveChildKey),
        this._store.select(selectSurveyId),
        this._store.select(selectSurveyFormState),
        this._store.select(selectSurvey),
        this._store.select(selectAdvancedFeasibilityFilters),
        this._store.select(selectPricing),
      ]),
      filter(([, activeChildKey, surveyId]) => !!activeChildKey && !!surveyId),
      map(
        ([
          applied,
          activeChildKey,
          surveyId,
          formState,
          survey,
          advancedFilters,
          pricing,
        ]) => ({
          allocations: applied.allocations,
          key: Option.from(activeChildKey).unwrap(),
          surveyId: Option.from(surveyId).unwrap(),
          survey: Option.from(survey),
          form: SurveyAudienceForm.from(formState),
          advancedFilters,
          offerPrice: appliedOfferPrice(applied, pricing.offerPrice),
        }),
      ),
      filter(({ form }) => form.valid),
      map(
        ({
          allocations,
          key,
          surveyId,
          survey,
          form,
          advancedFilters,
          offerPrice,
        }) => {
          const formData = Option.from(form.data).unwrap();
          return {
            allocations,
            key,
            advancedFilters,
            offerPrice,
            data: {
              user: this._withEmail(this._surveyUser(survey)),
              surveyId,
              survey: Option.from(survey),
              ...formData,
              catalogQualifications: this._catalogQualifications(
                formData.metadata,
              ),
            },
          };
        },
      ),
      switchMap(({ data, allocations, key, advancedFilters, offerPrice }) =>
        concat(
          of(
            SurveyCreationInsightsApiActions.calculateFeasibilityPending({
              key,
            }),
          ),
          this._loadFlexibilityPrices$(
            key,
            data,
            advancedFilters,
            offerPrice,
            allocations,
          ),
        ),
      ),
    ),
  );

  // `none` when Default Allocation is on but the active locale's defaults are
  // not in the supplier state yet: it still holds the previous locale's, and
  // spreading the goal by those would write the wrong quantities.
  private _redistributedAllocations(
    current: readonly SupplierAllocation[],
    goal: number,
  ): Option<SupplierAllocation[]> {
    if (
      this._surveyCreationSelectors.suppliersMetaData()
        .supplierDefaultAllocation
    ) {
      const defaultAllocations = this._activeLocaleDefaultAllocations();
      return defaultAllocations.length
        ? Option.some(
            this._supplierEngine.redistributeAllocationsByDefault(
              current,
              defaultAllocations,
              goal,
            ),
          )
        : Option.none;
    }

    return Option.some(
      this._resolveBlend().match({
        some: (blend) =>
          this._supplierEngine.redistributeAllocationsByBlend(
            current,
            blend,
            goal,
          ),
        none: () =>
          this._supplierEngine.redistributeAllocationsEvenly(current, goal),
      }),
    );
  }

  private _activeLocaleDefaultAllocations(): readonly DefaultSupplierAllocation[] {
    return this._surveyCreationSelectors
      .survey()
      .map((survey) =>
        defaultAllocationsOfLocale(
          this._supplierSelectors.defaultAllocations(),
          mapSurveyLocalization(survey.countryCode, survey.languageShortCode),
        ),
      )
      .unwrapOr([]);
  }

  // The applied blend, resolved from the loaded blends by the survey-creation
  // blend id; `none` when no blend is set or it isn't loaded — caller falls back
  // to even distribution.
  private _resolveBlend(): Option<SupplierBlend> {
    const blendId = this._surveyCreationSelectors.suppliersMetaDataBlendId();
    if (!blendId) return Option.none;
    return Option.from(
      this._supplierSelectors.blends().find((blend) => blend.id === blendId),
    );
  }

  // Needs a saved survey; pre-draft locales are `preDraftFeasibility$`'s job.
  private _recalculationTrigger$(): Observable<RecalculationTrigger> {
    return this._actions$.pipe(
      ofType(
        SurveyCreationCoreApiActions.loadActiveSurveyForEditSuccess,
        SurveyCreationInsightsActions.refreshAfterSave,
        SurveyCreationAudienceActions.updateMetadata,
        SurveyCreationAudienceActions.updateQualifications,
        SurveyCreationAudienceActions.updateQuotas,
        SurveyCreationAudienceActions.settleBuyerCpi,
        SurveyCreationChildrenActions.patchChildMetadata,
        SurveyCreationAudienceActions.applyAdvancedFilters,
        SurveyCreationAudienceActions.toggleManualProcessing,
        SurveyCreationCoreChildrenApiActions.loadChildSurveysSuccess,
        SurveyCreationChildrenApiActions.updateChildSurveyCpiSuccess,
      ),
      concatLatestFrom(() => this._store.select(selectRootSurveyId)),
      filter(([, rootSurveyId]) => !!rootSurveyId),
      map(([action]) => action),
      share(),
    );
  }

  // Same keys as the results, so no key is left stuck `loading`.
  private _recalculationPendingActions$(
    trigger$: Observable<RecalculationTrigger>,
  ): Observable<Action> {
    return trigger$.pipe(
      filter((action) => shouldMarkAsPending(action)),
      this._recalculationKeys(),
      map((key) =>
        SurveyCreationInsightsApiActions.calculateFeasibilityPending({ key }),
      ),
    );
  }

  // One pipeline per key, so one child's failure never blocks another.
  private _recalculationResultActions$(
    trigger$: Observable<RecalculationTrigger>,
  ): Observable<Action> {
    return trigger$.pipe(
      filter((action) =>
        qualifiesForRecalculation(
          action,
          !!this._userSettingsSelectors.buyerSettings().enableManualProcessing,
        ),
      ),
      this._recalculationKeys(),
      concatLatestFrom(() => [
        this._store.select(selectCoreChildren),
        this._store.select(selectAudienceChildren),
        this._store.select(selectInsightsChildren),
      ]),
      mergeMap(([key, coreChildren, audienceChildren, insightsChildren]) =>
        from(
          toRecalculationChildTasks(
            [key],
            coreChildren,
            audienceChildren,
            insightsChildren,
          ),
        ),
      ),
      groupBy(({ key }) => String(key)),
      mergeMap((task$) =>
        task$.pipe(
          switchMap(({ key, coreChild, audienceChild, offerPrice }) =>
            this._recalculateChildFeasibility$(
              key,
              coreChild,
              audienceChild,
              offerPrice,
            ),
          ),
        ),
      ),
    );
  }

  // Gated per child: siblings of one project can differ in status.
  private _recalculationKeys(): OperatorFunction<
    RecalculationTrigger,
    SurveyChildKey
  > {
    return (trigger$) =>
      trigger$.pipe(
        debounceByActionType(500),
        concatLatestFrom(() => [
          this._store.select(selectActiveChildKey),
          this._store.select(selectCoreChildren),
          this._store.select(selectInsightsChildren),
        ]),
        mergeMap(([action, activeChildKey, coreChildren, insightsChildren]) =>
          resolveRecalculationKeys(
            action,
            activeChildKey,
            coreChildren,
            insightsChildren,
          ).filter((key) =>
            this._insightGate.isAllowedForSurvey(
              INSIGHT.FEASIBILITY,
              Option.from(coreChildren[key]?.survey),
            ),
          ),
        ),
      );
  }

  private _recalculateChildFeasibility$(
    key: SurveyChildKey,
    coreChild: SurveyChildRegistryState,
    audienceChild: SurveyCreationChildAudienceState,
    offerPrice: Nullable<number>,
  ): Observable<Action> {
    const form = SurveyAudienceForm.from({
      metadata: audienceChild.metadata,
      qualifications: audienceChild.qualifications,
      quotas: audienceChild.quotas,
    });

    return Option.from(coreChild.survey)
      .zip(Option.from(form.data).filter(() => form.valid))
      .map(([survey, formData]) => ({ survey, formData }))
      .match({
        some: ({ survey, formData }) =>
          this._childCatalogQualifications$(formData.metadata).pipe(
            switchMap((catalogQualifications) => {
              const data: RecalculationData = {
                user: this._withEmail(this._surveyUser(Option.some(survey))),
                surveyId: survey.id,
                survey: Option.some(survey),
                ...formData,
                catalogQualifications,
              };
              return this._recalculatePricing$(
                key,
                data,
                audienceChild.advancedFeasibilityFilters,
                offerPrice,
              );
            }),
          ),
        none: () =>
          of(
            SurveyCreationInsightsApiActions.calculateFeasibilityFailure({
              key,
              error: CHILD_FEASIBILITY_UNAVAILABLE_MESSAGE,
            }),
          ),
      });
  }

  // A survey on system-managed PurePrice (defaultPurePriceOverride) is priced on
  // the pure price itself, so every audience change re-asks the pure-price
  // endpoint first and the feasibility is sent with that CPI as its offer price —
  // on the very first calculation too. The flag itself is left on; a manual Apply
  // is the only thing that turns it off (see the drawer mappers). Any other
  // survey prices on the offer price already known.
  private _recalculatePricing$(
    key: SurveyChildKey,
    data: RecalculationData,
    advancedFilters: AdvancedFeasibilityFilters,
    offerPrice: Nullable<number>,
  ): Observable<Action> {
    if (!this._isManagedPurePrice(key, data)) {
      return this._recalculateFlexibilityPricing$(
        key,
        data,
        advancedFilters,
        offerPrice,
      );
    }

    return this._purePriceOutcome$(data).pipe(
      switchMap(({ actions, cpi }) =>
        concat(
          from(actions),
          this._recalculateFlexibilityPricing$(
            key,
            data,
            advancedFilters,
            cpi.unwrapOr(offerPrice),
          ),
        ),
      ),
    );
  }

  // Pure price belongs to the survey being edited, not to its siblings.
  private _isManagedPurePrice(
    key: SurveyChildKey,
    data: RecalculationData,
  ): boolean {
    return (
      String(key) === String(this._surveyCreationSelectors.activeChildKey()) &&
      !!this._surveyCreationSelectors.suppliersMetaData()
        .defaultPurePriceOverride &&
      this._insightGate.isAllowedForSurvey(INSIGHT.PURE_PRICE, data.survey)
    );
  }

  // A failed fetch still lets the feasibility run, on the offer price already
  // known, rather than leaving the estimate stuck loading.
  private _purePriceOutcome$(
    data: RecalculationData,
  ): Observable<PurePriceOutcome> {
    return this._purePriceCpiService
      .compute(data.surveyId, this._purePriceParams(data))
      .pipe(
        map(
          (cpi): PurePriceOutcome => ({
            cpi: Option.some(cpi),
            actions: this._purePriceActions(cpi),
          }),
        ),
        this.psCatchError(
          (error): Observable<PurePriceOutcome> =>
            of({
              cpi: Option.none,
              actions: [
                SurveyCreationInsightsApiActions.loadPurePriceCpiFailure({
                  error: error.message,
                }),
              ],
            }),
          { notify: true },
        ),
      );
  }

  // The pure price is written in as the new global override — into insights
  // offerPrice and the audience suppliersMetaData.priceOverride.
  private _purePriceActions(cpi: number): Action[] {
    const allocations = this._supplierSelectors.allocations();
    return [
      SurveyCreationInsightsApiActions.loadPurePriceCpiSuccess({ cpi }),
      SurveyCreationAudienceActions.setSuppliersMetaData({
        metaData: { priceOverride: cpi },
      }),
      // Broadcast the same override CPI onto the supplier allocations,
      // persisted locally via loadAllocationsSuccess (no PUT) — the
      // redistribute pattern.
      ...(allocations.length
        ? [
            SupplierApiActions.loadAllocationsSuccess({
              allocations:
                this._supplierEngine.applyGlobalPriceOverrideToAllocations(
                  allocations,
                  cpi,
                ),
            }),
          ]
        : []),
    ];
  }

  // Pure price is priced off the audience only — the pricing / grouping /
  // supplier fields ComputeFeasibilityParams also carries are neutral here.
  private _purePriceParams(data: RecalculationData): ComputeFeasibilityParams {
    return {
      surveyId: data.surveyId,
      user: data.user,
      metadata: data.metadata,
      qualifications: data.qualifications,
      quotas: data.quotas,
      catalogQualifications: data.catalogQualifications,
      offerPrice: Option.none,
      supplierIds: [],
      surveyGroupIds: [],
      isExclusion: false,
      groupingStatuses: [],
    };
  }

  private _recalculateFlexibilityPricing$(
    key: SurveyChildKey,
    data: RecalculationData,
    advancedFilters: AdvancedFeasibilityFilters,
    offerPrice: Nullable<number>,
  ): Observable<Action> {
    return this._supplierService
      .allocations(data.surveyId, {
        scope: 'editable',
        context: toSupplierContext(data),
      })
      .pipe(
        switchMap((allocations) =>
          // Reflect the recalculation's supplier activate/deactivate flip into
          // the allocations slice (isActive only) before loading prices, without
          // discarding the user's other local edits.
          concat(
            this._storedAllocations$(key, allocations),
            this._loadFlexibilityPrices$(
              key,
              data,
              advancedFilters,
              offerPrice,
              allocations,
            ),
          ),
        ),
        this.psCatchError(
          (error) =>
            of(
              SupplierApiActions.loadAllocationsFailure({
                error: error.message ?? 'Failed to load supplier allocations',
              }),
            ),
          { notify: true },
        ),
      );
  }

  // The supplier state holds the active child's allocations only, so a
  // sibling's list never reaches it — in any family: merging it would add
  // suppliers the active survey does not have and flip its active flags.
  private _storedAllocations$(
    key: SurveyChildKey,
    allocations: SupplierAllocation[],
  ): Observable<Action> {
    return this._store.select(selectSurvey).pipe(
      concatLatestFrom(() => this._store.select(selectActiveChildKey)),
      take(1),
      filter(
        ([survey, activeChildKey]) =>
          !survey ||
          !SurveyKind.isMultiSurvey(survey) ||
          String(key) === String(activeChildKey),
      ),
      map(() => SupplierApiActions.syncAllocationsActiveState({ allocations })),
    );
  }

  private _loadFlexibilityPrices$(
    key: SurveyChildKey,
    data: RecalculationData,
    advancedFilters: AdvancedFeasibilityFilters,
    offerPrice: Nullable<number>,
    allocations: readonly SupplierAllocation[],
  ): Observable<Action> {
    return this._flexibilityPricesService.get(data.surveyId).pipe(
      concatLatestFrom(() => this._store.select(selectAudienceChildren)),
      map(([prices, audienceChildren]) =>
        this._withLocalOfferPrice(prices, audienceChildren, key),
      ),
      switchMap((prices) =>
        concat(
          of(
            SurveyCreationInsightsApiActions.calculateFlexibilityPricesSuccess({
              key,
              prices,
            }),
          ),
          this._computeFeasibility$(
            key,
            data,
            advancedFilters,
            this._feasibilityOfferPrice(offerPrice, prices),
            allocations,
          ),
        ),
      ),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationInsightsApiActions.calculateFlexibilityPricesFailure({
              key,
              error: error.message ?? 'Failed to load flexibility prices',
            }),
          ),
        { notify: true },
      ),
    );
  }

  // Apply is the commit boundary for Advanced Filters — the request is built
  // from what's already committed, not a second exclusion-free fetch.
  private _computeFeasibility$(
    key: SurveyChildKey,
    data: RecalculationData,
    advancedFilters: AdvancedFeasibilityFilters,
    offerPrice: Option<number>,
    allocations: readonly SupplierAllocation[],
  ): Observable<Action> {
    const params = computeFeasibilityParams(
      data,
      advancedFilters,
      activeSupplierIds(allocations),
      offerPrice,
    );

    return this._feasibilityService
      .compute(params, {
        notifyUnusedQualifications: this._insightGate.isAllowedForSurvey(
          INSIGHT.UNUSED_QUALIFICATION_NOTICE,
          data.survey,
        ),
      })
      .pipe(
        map((estimate) =>
          SurveyCreationInsightsApiActions.calculateFeasibilitySuccess({
            key,
            ...estimate,
          }),
        ),
        this._catchFeasibilityError(data, (error) =>
          SurveyCreationInsightsApiActions.calculateFeasibilityFailure({
            key,
            error,
          }),
        ),
      );
  }

  private _catchFeasibilityError(
    data: RecalculationData,
    toFailure: (error: string) => Action,
  ): OperatorFunction<Action, Action> {
    return this.psCatchError(
      (error) => {
        this._toastService.error(
          feasibilityErrorMessage(
            error.metadata['http'],
            data.quotas.map(({ quotaTitle }) => quotaTitle),
          ),
        );

        return of(toFailure(error.message));
      },
      { metadata: { notify: false } },
    );
  }

  // The backend answers with the override it has saved, which lags behind a
  // Buyer CPI the operator applied but has not saved yet. The child's own
  // override wins, so the price the recalculation writes back (and feasibility
  // is asked at) is the one the operator just set; no local override means the
  // saved one stands.
  private _withLocalOfferPrice(
    prices: FlexibilityPrices,
    audienceChildren: Readonly<
      Record<SurveyChildKey, SurveyCreationChildAudienceState>
    >,
    key: SurveyChildKey,
  ): FlexibilityPrices {
    return {
      ...prices,
      surveyPriceOverride: Option.from(audienceChildren[key])
        .andThen(({ suppliersMetaData }) =>
          Option.from(suppliersMetaData.priceOverride),
        )
        .filter((priceOverride) => priceOverride > 0)
        .unwrapOr(prices.surveyPriceOverride),
    };
  }

  /**
   * Feasibility prices against the buyer's override only when the buyer is
   * entitled to it; otherwise the override is discarded entirely rather than
   * quietly influencing the estimate. The offer price the caller resolved wins
   * as the latest the operator set; only without one does the saved global
   * override, read fresh with these prices, stand in — a survey just loaded has
   * nothing in the store yet.
   */
  private _feasibilityOfferPrice(
    offerPrice: Nullable<number>,
    prices: FlexibilityPrices,
  ): Option<number> {
    return this._userSettingsSelectors.buyerSettings()
      .enablePriceOverrideFeasibilityMultiplier
      ? Option.from(offerPrice)
          .filter((price) => price > 0)
          .orElse(() => Option.from(prices.surveyPriceOverride))
      : Option.none;
  }

  /** Shares one in-flight `computeForPreDraft` call across byte-identical params. */
  private _sharedFeasibilityRequest(
    cache: Map<string, Observable<FeasibilityEstimate>>,
    params: ComputeFeasibilityParamsForPreDraft,
  ): Observable<FeasibilityEstimate> {
    const hash = JSON.stringify(params);
    const cached = cache.get(hash);

    if (cached) {
      return cached;
    }

    const request$ = this._feasibilityService
      .computeForPreDraft(params)
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    cache.set(hash, request$);
    return request$;
  }

  private _currentUser(): FeasibilityRequester {
    return {
      id: this._authSelectors.user().mapOr(undefined, ({ id }) => id),
      companyId: this._authSelectors.activeCompanyId(),
    };
  }

  // The survey's own buyer, whoever has it open.
  private _surveyUser(survey: Option<Survey>): FeasibilityRequester {
    return {
      id: survey.mapOr(undefined, ({ buyerUserId }) => buyerUserId),
      companyId: survey.mapOr(
        undefined,
        ({ buyerCompanyId }) => buyerCompanyId,
      ),
    };
  }

  private _withEmail(
    user: FeasibilityRequester,
  ): ComputeFeasibilityParams['user'] {
    return {
      ...user,
      email: this._authSelectors.user().mapOr('', ({ email }) => email),
    };
  }

  // Unfiltered catalog (232 included) — the survey's own qualifications never
  // carry a 232 entry, see RecalculationData.catalogQualifications.
  private _catalogQualifications(
    metadata: SurveyMetadataForm,
  ): Qualification[] {
    return this._surveyCreationSelectors.selectQualifications(
      {
        countryCode: metadata.selectedCountries[0] ?? '',
        languageShortCode: metadata.selectedLanguages[0] ?? '',
      },
      true,
    );
  }

  private _childCatalogQualifications$(
    metadata: SurveyMetadataForm,
  ): Observable<Qualification[]> {
    const cached = this._catalogQualifications(metadata);
    if (cached.length) {
      return of(cached);
    }

    const countryCode = metadata.selectedCountries[0] ?? '';
    const languageShortCode = metadata.selectedLanguages[0] ?? '';

    this._store.dispatch(
      SurveyCreationReferenceActions.loadQualifications({
        countryCode,
        languageShortCode,
      }),
    );

    return this._actions$.pipe(
      ofType(
        SurveyCreationReferenceApiActions.loadQualificationsSuccess,
        SurveyCreationReferenceApiActions.loadQualificationsFailure,
      ),
      filter(
        (action) =>
          action.countryCode === countryCode &&
          action.languageShortCode === languageShortCode,
      ),
      take(1),
      map(() => this._catalogQualifications(metadata)),
    );
  }
}
